import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SkipReason, TaskNextStep, TaskPriority, TaskType } from '@brimo/shared';
import { ErrorCode } from '@brimo/shared';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types of `GET /tasks`, `GET /tasks/:id` and the task actions (docs/dev/api-tasks.md). */

export interface UserRef {
  id: string;
  name: string;
  initials: string;
}

export interface TaskDue {
  on: string | null;
  time: string | null;
  label: string;
  overdue: boolean;
}

export interface StuckInfo {
  reason: 'skipped' | 'overdue' | 'inactive';
  since: string;
  label: string;
  previousAssignee: UserRef | null;
  escalation: { user: UserRef; label: string } | null;
}

export interface TaskItem {
  id: string;
  priority: TaskPriority;
  type: TaskType;
  typeLabel: string;
  cta: string;
  title: string;
  reference: string;
  due: TaskDue;
  assignee: UserRef;
  contact: { id: string; name: string } | null;
  contactId: string | null;
  dealId: string | null;
  financingId: string | null;
  propertyId: string | null;
  ticketId: string | null;
  appointmentId: string | null;
  approvalId: string | null;
  status: 'open' | 'done' | 'closed';
  skipCount: number;
  stuck: StuckInfo | null;
  href: string;
}

export interface SkipEntry {
  at: string;
  reason: SkipReason;
  reasonLabel: string;
  note: string | null;
  by: UserRef | null;
  label: string;
}

export interface TaskDetail extends TaskItem {
  description: string | null;
  source: 'manual' | 'automation' | 'rule' | 'system';
  createdAt: string;
  createdBy: UserRef | null;
  unitId: string | null;
  reservationId: string | null;
  followUpOfId: string | null;
  visibleToCustomer: boolean;
  snoozedUntilAt: string | null;
  outcome: { type: string | null; text: string | null; at: string | null; by: UserRef | null } | null;
  closedReason: 'dnc' | 'deal_lost' | null;
  skips: SkipEntry[];
  approval: {
    id: string;
    type: string;
    typeLabel: string;
    status: 'open' | 'approved' | 'rejected' | 'withdrawn';
    requestedBy: UserRef | null;
    message: string | null;
    href: string;
  } | null;
  attachments: Array<{ id: string; fileId: string; name: string; mime: string; size: number }>;
}

export interface QuickPick {
  key: 'tomorrow' | 'in_3_days' | 'next_week' | 'free';
  label: string;
  on: string | null;
}

export interface NextDue {
  on: string;
  time: string | null;
  label: string;
}

export interface TasksResponse {
  today: string;
  user: UserRef;
  header: {
    open: number;
    overdue: number;
    openTotal: number;
    doneToday: number;
    doneTodayByGroup: { calls: number; messages: number; approvals: number; other: number };
    nextDue: NextDue | null;
  };
  blocks: {
    stuck: TaskItem[] | null;
    overdue: TaskItem[];
    today: TaskItem[];
    later: TaskItem[] | null;
    laterCount: number;
  };
  quickPicks: QuickPick[];
  /** Users the viewer may assign tasks to (me, my span, the back office; admins everyone). */
  assignees?: UserRef[] | undefined;
}

export interface CompleteTaskBody {
  outcomeType?: string | null;
  outcomeText?: string | null;
  nextStep?: TaskNextStep;
}

export interface CompleteTaskResponse {
  task: TaskDetail;
  next: 'book_appointment' | null;
  createdTaskId: string | null;
}

export interface SnoozeBody {
  dueOn: string;
  dueTime?: string | null;
}

export type ReassignMode = 'take_over' | 'give_back' | 'assign' | 'escalate';

export interface ReassignBody {
  mode: ReassignMode;
  assigneeId?: string;
  reason?: string | null;
}

export interface SkipBody {
  reason: SkipReason;
  note?: string | null;
}

export interface SkipResponse {
  skipCount: number;
  stuck: boolean;
  assigneeId: string;
  task: TaskDetail;
}

export interface CreateTaskBody {
  title: string;
  description?: string | null;
  type?: TaskType;
  priority?: TaskPriority;
  dueOn?: string | null;
  dueTime?: string | null;
  assigneeId?: string;
  contactId?: string | null;
}

export const tasksListKey = (later: boolean) => [...queryRoots.tasks, 'list', { later }] as const;
export const taskDetailKey = (id: string) => [...queryRoots.tasks, 'detail', id] as const;

/** „Meine Tasks" (TQ-01…05, BO-01, FZ-01/02); `later` adds the „Später" block (TQ-03). */
export function useTasks(later: boolean) {
  return useQuery({
    queryKey: tasksListKey(later),
    queryFn: () => api<TasksResponse>(`/tasks?later=${later ? '1' : '0'}`),
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: taskDetailKey(id ?? ''),
    queryFn: () => api<TaskDetail>(`/tasks/${id ?? ''}`),
    enabled: id !== null,
  });
}

/** Refreshes every list, detail and queue query after a task changed. */
export function useInvalidateTasks() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.queue }),
      queryClient.invalidateQueries({ queryKey: queryRoots.session }),
    ]);
  };
}

export function useSnoozeTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SnoozeBody }) =>
      api<TaskDetail>(`/tasks/${id}/snooze`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useCompleteTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CompleteTaskBody }) =>
      api<CompleteTaskResponse>(`/tasks/${id}/complete`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useReassignTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReassignBody }) =>
      api<TaskDetail>(`/tasks/${id}/reassign`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useSkipTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SkipBody }) =>
      api<SkipResponse>(`/tasks/${id}/skip`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useCreateTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (body: CreateTaskBody) => api<TaskDetail>('/tasks', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/** `409 NEXT_STEP_REQUIRED` — the SPA shows „Nächster Schritt fehlt" (Queue 07) and resubmits. */
export function isNextStepRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.NEXT_STEP_REQUIRED;
}

/** The route the SPA opens after a `book_appointment` next step: the contact's appointment editor. */
export function bookAppointmentHref(contactId: string, interimTaskId: string | null): string {
  const params = new URLSearchParams({ editor: 'appointment' });
  if (interimTaskId !== null) params.set('task', interimTaskId);
  return `/contacts/${contactId}?${params.toString()}`;
}
